/* The Consumption card's figures, computed in ONE place.
 *
 * ── Why this is a module rather than a function in a chart ────────────────
 * Two components published the Consumption card: Summary/OverlayChart.jsx and
 * ConsumptionDashboard/ConsumptionChart.jsx. Each had a local function called
 * `calculateTotalAreaAndCost`, and the two had drifted apart:
 *
 *   OverlayChart's preferred the engine's own `Totals Consumption Cost ($)`
 *     when the file carries it, summed `Building Load Load Cost ($)` as a
 *     separate row figure, and reported column ABSENCE as null.
 *
 *   ConsumptionChart's always summed `Building Load Load Cost ($)` into the
 *     total, and published no `loadCost` at all.
 *
 * `setExportedData` is a full REPLACE, not a merge, and both wrote the same
 * app-level slot. So the Consumption card's total changed value depending on
 * which tab rendered last, and visiting the Consumption tab stripped `loadCost`
 * out of the payload -- which is why the Load row's money was an em-dash on the
 * very page that owns that card, carrying the "re-run to populate it" hint for
 * a column that is present in every result file (confirmed: `Building Load Load
 * Cost ($)` is in the husky-2040-prod header). Wrong advice, from a missing
 * field rather than a missing column.
 *
 * One function, one rule, both callers. That closes the missing field, the
 * two-rule divergence and the last-writer-wins leak together.
 */

import {
  startColumnSum,
  addColumnValue,
  finishColumnSum,
  hasBackendTotalsColumn,
  sumBackendColumn,
} from "./columnSums";

/* The engine's site-level Totals columns for consumption. Preferred over a
 * per-row reconstruction whenever the file carries them. */
export const CONSUMPTION_TOTALS_COLUMNS = {
  energy: "Totals Total Consumption (kWh)",
  cost: "Totals Consumption Cost ($)",

  /* NOT a Totals column. Building load is a meter rather than a device, so the
   * engine writes it under the meter's own group -- and it has been written
   * there all along, which is why this row could have been populated before any
   * of the others. It was already being read, but only inside the legacy
   * `!useBackendCost` branch and only into the card TOTAL, so on any result
   * carrying the Totals columns (every modern one) the read was skipped
   * entirely and the figure never reached the row. */
  loadCost: "Building Load Load Cost ($)",
};

/* The load column, under either name the corpus uses. Resolved in order and by
 * EXACT match, matching what columnLookup's readColumn did here before -- the
 * first of the two names PRESENT in the row wins, even if its cell is empty. */
export const CONSUMPTION_LOAD_COLUMNS = [
  "Load (kWh)",
  "Building Load Load (kWh)",
];

const isPresent = (value) =>
  value !== undefined && value !== null && value !== "";

/** The row's load cell under whichever of the two names the row carries. */
const readLoadCell = (entry) => {
  if (!entry || typeof entry !== "object") return undefined;
  for (const key of CONSUMPTION_LOAD_COLUMNS) {
    if (key in entry) return entry[key];
  }
  return undefined;
};

/**
 * Total energy, total cost, peak power and the Load row's money for a set of
 * result rows.
 *
 * Every figure is a fixed-2 string or null. Null means "no column here", which
 * the cards render as an em-dash; "0.00" means the engine measured zero.
 *
 * @param {Array<object>} data - the period's rows.
 * @returns {{totalArea: string|null, totalCost: string, maxPower: string|null,
 *            loadCost: string|null}}
 */
export function calculateConsumptionTotals(data) {
  if (!data || data.length === 0) {
    return {
      totalArea: "0.00",
      totalCost: "0.00",
      maxPower: "0.00",
      loadCost: null,
    };
  }

  // Prefer the backend `Totals` columns at the dataset level. Power max has no
  // backend equivalent and stays sourced from the per-row Load column.
  const useBackendEnergy = hasBackendTotalsColumn(
    data,
    CONSUMPTION_TOTALS_COLUMNS.energy,
  );
  const useBackendCost = hasBackendTotalsColumn(
    data,
    CONSUMPTION_TOTALS_COLUMNS.cost,
  );

  let totalArea = 0;
  let maxPower = 0;
  let totalCost = 0;

  /* Presence for the LOAD column, tracked exactly the way startColumnSum tracks
   * it for a money column.
   *
   * Without this, `maxPower` was the last cell on this card that could still
   * fabricate: with neither load column in the file every row read as 0, the
   * running max settled on 0, and the card published a measured-looking "0.00"
   * into its POWER cell while the cells beside it correctly showed absence.
   * Energy has the same exposure whenever the Totals column is missing too, so
   * both are gated on the same flag. */
  let sawLoad = false;

  /* The Load row's money. Summed unconditionally, because a column being "not
   * persisted" is worth checking against the file rather than inferred from the
   * cell being empty: an empty cell means only that nothing READ it. */
  const loadCost = startColumnSum();

  for (const entry of data) {
    const rawLoad = readLoadCell(entry);
    if (isPresent(rawLoad)) sawLoad = true;
    const numeric = parseFloat(rawLoad);
    const absY = Math.abs(Number.isFinite(numeric) ? numeric : 0);
    if (absY > maxPower) maxPower = absY;

    if (!useBackendEnergy) totalArea += absY;

    addColumnValue(loadCost, entry, CONSUMPTION_TOTALS_COLUMNS.loadCost);

    if (!useBackendCost) {
      /* Read, never reconstructed. A costed 0 is a measurement: the hour was
       * served from storage or solar and the engine billed nothing for it.
       * Multiplying load by the buy price for those hours re-charged an
       * internally-routed flow at the meter - the double charge cardFlowRows.js
       * forbids by name. Absence is carried by the loadCost sum above. */
      const cost = parseFloat(entry?.[CONSUMPTION_TOTALS_COLUMNS.loadCost]);
      if (Number.isFinite(cost)) totalCost += cost;
    }
  }

  if (useBackendEnergy) {
    totalArea = sumBackendColumn(data, CONSUMPTION_TOTALS_COLUMNS.energy);
  }
  if (useBackendCost) {
    totalCost = sumBackendColumn(data, CONSUMPTION_TOTALS_COLUMNS.cost);
  }

  return {
    /* Consumption is drawn and reported negative - it is energy leaving the
     * site's account - which is why both of these are negated. */
    totalArea:
      useBackendEnergy || sawLoad ? (-totalArea).toFixed(2) : null,
    totalCost: totalCost.toFixed(2),
    maxPower: sawLoad ? (-maxPower).toFixed(2) : null,
    loadCost: finishColumnSum(loadCost),
  };
}
